import React, { useState } from "react";
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import Typography from '@mui/material/Typography';
import { Box, Card, CardContent, Tooltip, Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper, TextField, TableSortLabel, Button, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ResumeDialog from './ResumeDialog'; // Reuse the Resume-like dialog component
import CertificateDialog from './CertificateDialog';

const useStyles = makeStyles(() => ({
  card: {
    maxWidth: '100%',
    '@media (max-width: 600px)': {
      maxWidth: '100%',
    },
  },
  infoIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Experience() {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [expandedDialog, setExpandedDialog] = useState<string | false>(false);
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<{ name: string; completedDate: string; details: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 3;

  const [openTataSteelDialog, setOpenTataSteelDialog] = useState(false);
  const [openResearchDialog, setOpenResearchDialog] = useState(false);
  const [openITCCertificatesDialog, setOpenITCCertificatesDialog] = useState(false);
  const [openPwCCertificatesDialog, setOpenPwCCertificatesDialog] = useState(false);
  const [openTATAAMCCertificatesDialog, setOpenTATAAMCCertificatesDialog] = useState(false);

  const handleOpenTataSteelDialog = () => setOpenTataSteelDialog(true);
  const handleCloseTataSteelDialog = () => setOpenTataSteelDialog(false);

  const handleOpenResearchDialog = () => setOpenResearchDialog(true);
  const handleCloseResearchDialog = () => setOpenResearchDialog(false);

  const handleOpenITCCertificatesDialog = () => setOpenITCCertificatesDialog(true);
  const handleCloseITCCertificatesDialog = () => setOpenITCCertificatesDialog(false);

  const handleOpenPwCCertificatesDialog = () => setOpenPwCCertificatesDialog(true);
  const handleClosePwCCertificatesDialog = () => setOpenPwCCertificatesDialog(false);

  const handleOpenTATAAMCCertificatesDialog = () => setOpenTATAAMCCertificatesDialog(true);
  const handleCloseTATAAMCCertificatesDialog = () => setOpenTATAAMCCertificatesDialog(false);

  interface Certificate {
    title: string;
    type: "image" | "pdf";
    file: string;
  }

  interface DialogContent {
    title: string;
    details: string;
    impacts?: { title: string; description: string; technologies?: { href: string; src: string; alt: string }[] }[];
  }

  interface Experience {
    title: string;
    subtitle: string;
    client?: string;
    description?: string;
    dialogContent: DialogContent;
    technologies?: { href: string; src: string; alt: string }[];
  }

  const [dialogContent, setDialogContent] = useState<DialogContent | null>(null);

  const handleClickOpen = (content: DialogContent) => {
    setDialogContent(content);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDialogContent(null);
    setExpandedDialog(false);
  };

  const handleDialogAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedDialog(isExpanded ? panel : false);
  };

  const handleTrainingDialogOpen = (training: { name: string; completedDate: string; details: string }) => {
    setSelectedTraining(training);
    setTrainingDialogOpen(true);
  };

  const handleTrainingDialogClose = () => {
    setTrainingDialogOpen(false);
    setSelectedTraining(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenPdf = (pdfUrl: string) => {
    window.open(pdfUrl, '_blank');
  };

  const experiences = [
    {
      title: "Software Engineer Generative AI Lead",
      subtitle: "TATA Asset Management Corporation",
      description: "Full Stack Generative AI Developer",
      dialogContent: {
        title: "TATA AMC Summary",
        details: "SUMMARY OF WORK DONE WITH TATA AMC",
        impacts: [
          {
            title: "TARA - TATA AMC GenAI Platform",
            description: `TARA acts as an end-to-end AI Copilot platform serving Product, Research, and Trade teams with comprehensive document intelligence, semantic search, real-time analytics, and automated compliance workflows.

PROJECT SCOPE:
Architected and led full-stack development of TARA, an enterprise-grade AI Copilot platform with document intelligence, semantic search, real-time analytics, and automated compliance workflows.

ROLE: Solution Architect & Full-Stack Generative AI Engineer

TECHNOLOGY STACK: LangChain, LlamaIndex, OpenAI, Azure OpenAI, Hugging Face, Ollama, Chroma DB, Python, FastAPI, React JS, NextJS, Docker, EC2, Lambda, ECS, S3, EFS, DynamoDB, DocumentDB, ElastiCache (Redis), Kinesis, API Gateway, VPC, Route 53, Transit Gateway, IAM, KMS, Secrets

═══════════════════════════════════════════════════════════════════

1. PRODUCT COPILOT (Communication & Document Intelligence)
Features:
• AI-assisted document creation & editing (memos, reports, emails)
• Template-based communication generation
• Document search via tags, metadata, and semantic queries
• Versioning and structured document management
• AI-powered formatting and summarization

Client Benefits:
⏱️ 60–80% faster document creation
📄 70% reduction in manual formatting effort
🔍 3x faster document retrieval
👥 30–40% productivity gain for business teams

═══════════════════════════════════════════════════════════════════

2. RESEARCH COPILOT (Qualitative & Quantitative Insights)

A. Qualitative Insights:
Features: Earnings report & filings analysis, News summarization + sentiment detection, Research document intelligence, Chat with documents (RAG-based)
Client Benefits: ⏱️ 70–90% reduction in research time, 📄 80% faster document review, 📊 30–40% improvement in insight quality, 👨‍💻 3–5x analyst productivity

B. Quantitative Analytics:
Features: Financial dashboards (charts + tables), Company benchmarking & valuation analysis, Interactive visualizations
Client Benefits: 📉 60–70% reduction in manual analysis, ⚡ 2x faster decision-making, 📊 Real-time insights vs delayed reporting

C. Report Generation:
Features: Automated multi-company report builder, Quarterly/FY report generation, Downloadable documents (DOCX/PDF)
Client Benefits: ⏱️ 75–90% reduction in report generation time, 📑 From hours → minutes, 👥 ~40% operational bandwidth saved

D. Chat & Knowledge Management:
Features: Conversational AI interface, Chat history (pin, rename, delete), Context-aware responses, Knowledge reuse across sessions
Client Benefits: 🔁 50% reduction in repeated work, ⚡ Faster onboarding of new users, 📚 Improved institutional knowledge retention

═══════════════════════════════════════════════════════════════════

3. TRADE COPILOT (Pre → Post Trade Lifecycle)

A. Pre-Trade AI:
Features: Earnings analysis, News aggregation & summarization, Research intelligence
Client Benefits: ⏱️ 80–90% reduction in research effort, 📊 5–10x coverage increase per analyst, 🎯 Higher-quality investment decisions

B. At-Trade AI:
Features: Real-time market monitoring, Pattern & anomaly detection
Client Benefits: ⚡ Real-time decision support, 📈 20–30% better opportunity capture, 🚨 Up to 50% faster anomaly detection

C. Post-Trade AI:
Features: Trade confirmation extraction (PDF/email/API), Settlement reconciliation automation
Client Benefits: 🤖 80–90% automation of reconciliation, ❌ 60–80% reduction in errors, ⏱️ 50–70% faster settlement cycles

D. Compliance Automation:
Features: KYC / AML checks, Regulatory validation, Audit trail generation
Client Benefits: 🛡️ 90%+ compliance accuracy, ⏱️ 70% reduction in manual compliance effort, 📑 Fully audit-ready workflows

E. Client Servicing:
Features: Automated trade query handling, Portfolio summaries & insights
Client Benefits: 📞 60–80% reduction in support workload, ⚡ Instant client responses, 😊 Improved client satisfaction & turnaround time

═══════════════════════════════════════════════════════════════════

OVERALL BUSINESS IMPACT (End-to-End TARA Platform):
🚀 3–5x overall productivity improvement
⏱️ 70–85% reduction in manual effort
❌ Up to 80% reduction in operational errors
📊 2–3x faster decision-making
👥 30–50% cost efficiency in operations & research
📈 Ability to scale without proportional headcount increase

KEY ACHIEVEMENTS:
• Designed and delivered an AI Copilot platform serving 3 major business verticals with differentiated use cases and UX
• Built semantic search and RAG capabilities enabling document intelligence across earnings reports, research, and trade data
• Implemented real-time analytics dashboards for financial insights and benchmarking
• Developed automated compliance workflows for KYC, AML, and trade settlement reconciliation
• Architected scalable microservices infrastructure on AWS supporting multi-tenant deployment
• Delivered admin dashboards for usage analytics, compliance audit trails, and operational governance`,
            technologies: [
              { href: "https://aws.amazon.com/dynamodb/", src: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png", alt: "AWS DynamoDB" },
              { href: "https://aws.amazon.com/s3/", src: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png", alt: "AWS S3" },
              { href: "https://aws.amazon.com/ecs/", src: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png", alt: "AWS ECS" },
              { href: "https://reactjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original-wordmark.svg", alt: "React JS" },
              { href: "https://nextjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/nextjs/nextjs-original-wordmark.svg", alt: "NextJS" },
              { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python FastAPI" },
              { href: "https://www.llamaindex.ai/", src: "https://avatars.githubusercontent.com/u/128737003?s=200&v=4", alt: "LlamaIndex" },
            ],
          },
        ],
      },
    },
    {
      title: "Associate Consultant",
      subtitle: "ITC Infotech",
      description: "Full Stack Generative AI Developer",
      dialogContent: {
        title: "ITC Infotech Summary",
        details: "SUMMARY OF WORK DONE WITH ITC INFOTECH",
        impacts: [
          {
            title: "Full-Stack GenAI Enterprise Platform",
            description: `• Project Scope: Led the design and development of an enterprise-grade conversational AI platform featuring intelligent chat sessions, document processing and analysis, workspace management, semantic search capabilities, and integration with external data sources.

• Role: Solution Architect & Full-Stack AI Engineer

• Technology: LangChain, LlamaIndex, OpenAI, Azure OpenAI, Hugging Face, Ollama, Chroma DB, Python, FastAPI, React JS, NextJS, Docker, EC2, Lambda, ECS, S3, EFS, DynamoDB, DocumentDB, ElastiCache (Redis), Kinesis, API Gateway, VPC, Route 53, Transit Gateway, IAM, KMS, Secrets

• Professional Enhancement: Software Design Patterns & Object Oriented Design, Software Design & Development, Foundations of Computer Science, Data Structures & Algorithms, Numerical Methods, Probability & Statistics, Introduction to Machine Learning, Neural Networks & Deep Learning

• Built a full-stack GenAI platform with multi-session chat, workspace-based organization, and intelligent document handling.

• Developed the frontend in React + NextJS, delivering a clean, responsive chat UX with session history and workspace navigation.

• Implemented semantic search and contextual analysis capabilities for enhanced user interactions.

• Delivered admin tools to track prompt usage, token metrics, and user activity, ensuring governance and transparency.

• Architected secure, scalable infrastructure on AWS using serverless, containerized, and streaming services`,
            technologies: [
              { href: "https://aws.amazon.com/dynamodb/", src: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png", alt: "AWS DynamoDB" },
              { href: "https://aws.amazon.com/s3/", src: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png", alt: "AWS S3" },
              { href: "https://aws.amazon.com/ecs/", src: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png", alt: "AWS ECS" },
              { href: "https://reactjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original-wordmark.svg", alt: "React JS" },
              { href: "https://nextjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/nextjs/nextjs-original-wordmark.svg", alt: "NextJS" },
              { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python FastAPI" },
              { href: "https://www.llamaindex.ai/", src: "https://avatars.githubusercontent.com/u/128737003?s=200&v=4", alt: "LlamaIndex" },
            ],
          },
        ],
      },
    },
    {
      title: "CTA",
      subtitle: "PwC US AC",
      description: "Associate Solution Architect, Data Scientist & Full Stack Developer Global and Foreign Advisory",
      dialogContent: {
        title: "PwC Work Summary",
        details: "SUMMARY OF WORK DONE AT PWC",
        impacts: [
          {
            title: "External Client: Foreign Policies",
            description: "Using .NET, React, and SQL to administer as a technical admin across various change requests and working across pillars.",
            technologies: [
              { href: "https://azure.microsoft.com/en-us/services/devops/", src: "https://learn.microsoft.com/en-us/media/logos/logo-ms-social.png", alt: "Azure DevOps" },
              { href: "https://dotnet.microsoft.com/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/dot-net/dot-net-original-wordmark.svg", alt: ".NET" },
              { href: "https://reactjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original-wordmark.svg", alt: "React" },
              { href: "https://www.microsoft.com/en-us/sql-server", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/microsoftsqlserver/microsoftsqlserver-plain-wordmark.svg", alt: "SQL Server" },
            ],
          },
          {
            title: "PwC Learning & Developments",
            description: "Achieved certifications for all PwC Full Stack Programmes, .NET, Java & Spring Framework, NodeJS, React & Angular Boot Camps.",
            technologies: [
              { href: "https://angular.io", src: "https://angular.io/assets/images/logos/angular/angular.svg", alt: "Angular" },
              { href: "https://nodejs.org", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/nodejs/nodejs-original-wordmark.svg", alt: "Node.js" },
              { href: "https://spring.io/projects/spring-boot", src: "https://www.vectorlogo.zone/logos/springio/springio-icon.svg", alt: "Spring Boot" },
            ],
          },
          {
            title: "External Client: A Healthcare Diagnostics Company",
            description: "Solution Architect, Data Scientist, and Full Stack Developer role for one of the largest healthcare diagnostics centers in India.",
            technologies: [
              { href: "https://azure.microsoft.com/en-us/services/devops/", src: "https://learn.microsoft.com/en-us/media/logos/logo-ms-social.png", alt: "Azure DevOps" },
              { href: "https://www.typescriptlang.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg", alt: "TypeScript" },
              { href: "https://dotnet.microsoft.com/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/dot-net/dot-net-original-wordmark.svg", alt: ".NET" },
              { href: "https://reactjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original-wordmark.svg", alt: "React" },
              { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python" },
            ],
          },
          {
            title: "Microsoft Azure Devboards Product Release Notes Plugin",
            description: "Full Stack Developer role for Microsoft Azure Devboards Product Management, Release Notes Plugin using Semantic Kernel, Python, Azure, React, OpenAPI.",
            technologies: [
              { href: "https://www.typescriptlang.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg", alt: "TypeScript" },
              { href: "https://azure.microsoft.com/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/azure/azure-original.svg", alt: "Azure" },
              { href: "https://reactjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original-wordmark.svg", alt: "React" },
              { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python" },
              { href: "https://aka.ms/semantic-kernel", src: "https://avatars.githubusercontent.com/u/6154722?s=200&v=4", alt: "Semantic Kernel" },
              { href: "https://www.openapis.org/", src: "https://avatars.githubusercontent.com/u/6844498?s=200&v=4", alt: "OpenAPI" },
            ],
          },
          {
            title: "Absolute Systems and Functions",
            description: "Used Power Query, Microsoft Dataverse, Excel, and LeanIX to perform business data cutovers and transformations, ensuring seamless data migration and integration.",
            technologies: [
              { href: "https://learn.microsoft.com/en-us/power-apps/maker/data-platform/data-platform-intro", src: "https://learn.microsoft.com/en-us/media/logos/logo-ms-social.png", alt: "Dataverse" },
              { href: "https://www.microsoft.com/en-us/microsoft-365/excel", src: "https://cdn.worldvectorlogo.com/logos/microsoft-excel-2013.svg", alt: "Excel" },
            ],
          },
          {
            title: "Concourse 2.0",
            description: "Played a key role in migrating from Monolith to Event-Based Module Federated Sub-Module Distributed Micro-Front Ends.",
            technologies: [
              { href: "https://www.typescriptlang.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg", alt: "TypeScript" },
              { href: "https://reactjs.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original-wordmark.svg", alt: "React" },
              { href: "https://webpack.js.org/", src: "https://raw.githubusercontent.com/webpack/media/master/logo/icon-square-big.png", alt: "Webpack" },
              { href: "https://azure.microsoft.com/en-in/", src: "https://www.vectorlogo.zone/logos/microsoft_azure/microsoft_azure-icon.svg", alt: "Azure" },
            ],
          },
          {
            title: "AI Factory: PwC Digital Generative AI Initiative",
            description: "Created Proof of Concept for end-to-end application development and deployment using ChatGPT from OpenAI.",
            technologies: [
              { href: "https://openai.com/chatgpt", src: "https://avatars.githubusercontent.com/u/78924801?s=200&v=4", alt: "ChatGPT" },
              { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python" },
            ],
          },
          {
            title: "Concourse User Story Coach",
            description: "Developed a user story coaching tool using Flask and other Python frameworks as the backend, integrated with Concourse 2.0 for enhanced user story management.",
            technologies: [
              { href: "https://flask.palletsprojects.com/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/flask/flask-original.svg", alt: "Flask" },
              { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python" },
            ],
          },
        ],
      },
    },
    {
      title: "Project Intern",
      subtitle: "Tata Steel, Jamshedpur, Jharkhand",
      description: "Pseudo defect reduction in Surface Inspection System at CRM Bara",
      dialogContent: {
        title: "Tata Steel Project",
        details: "Implemented advanced algorithms to reduce pseudo defects in the Surface Inspection System at CRM Bara, leading to improved production quality and reduced downtime.",
      },
      technologies: [
        { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python" },
        { href: "https://opencv.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/opencv/opencv-original.svg", alt: "OpenCV" },
      ],
    },
    {
      title: "Research Project Member",
      subtitle: "Institute of Technical Education and Research, Bhubaneswar",
      description: "Breast Cancer Classification using Deep Neural Network",
      dialogContent: {
        title: "Breast Cancer Classification",
        details: "Developed a deep learning model for breast cancer classification, achieving high accuracy in early detection and aiding in better diagnostic decisions.",
      },
      technologies: [
        { href: "https://www.tensorflow.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/tensorflow/tensorflow-original.svg", alt: "TensorFlow" },
        { href: "https://pytorch.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/pytorch/pytorch-original.svg", alt: "PyTorch" },
        { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python" },
      ],
    },
    {
      title: "Teaching Assistant",
      subtitle: "University of Minnesota, Morris",
      description: "Introduction to Digital Media Computation using Python Programming Language",
      dialogContent: {
        title: "Teaching Assistant Role",
        details: "Assisted in teaching Python programming for digital media computation, enhancing student engagement and understanding through interactive sessions.",
      },
      technologies: [
        { href: "https://www.python.org/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg", alt: "Python" },
      ],
    },
    {
      title: "Research Project Member",
      subtitle: "University of Minnesota, Morris",
      description: "Power Consumption Project that shows power usage around University in an Android, iOS, and Web-based Application",
      dialogContent: {
        title: "Power Consumption Project",
        details: "Developed a cross-platform application to monitor and visualize power usage across the university, promoting energy efficiency and sustainability.",
      },
      technologies: [
        { href: "https://reactnative.dev/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original-wordmark.svg", alt: "React Native" },
        { href: "https://firebase.google.com/", src: "https://www.vectorlogo.zone/logos/firebase/firebase-icon.svg", alt: "Firebase" },
        { href: "https://developer.android.com/", src: "https://raw.githubusercontent.com/devicons/devicon/master/icons/android/android-original.svg", alt: "Android" },
        { href: "https://developer.apple.com/ios/", src: "https://developer.apple.com/assets/elements/icons/ios/ios-128x128_2x.png", alt: "iOS" },
      ],
    },
  ];
  
  const itcCertificates = [
    { title: "ITC Infotech Certificate of Recognition", type: "image" as const, file: "/c0.png" },
  ];

  const pwcCertificates = [
    { title: "PwC D'Art of Disruption", type: "image" as const, file: "/c1.jpeg" },
    { title: "AWS Partner Cloud Economics Accrediation", type: "pdf" as const, file: "/c2.pdf" },
    { title: "AWS Partner Accrediation Business Digital", type: "pdf" as const, file: "/c3.pdf" },
    { title: "AWS Certified Cloud Practitioner", type: "pdf" as const, file: "/c4.pdf" },
    { title: "AWS Certified Solution Architect", type: "pdf" as const, file: "/c5.pdf" },
    { title: "PwC NodeJS Learning Program", type: "pdf" as const, file: "/c6.pdf" },
    { title: "Linkedin Learning: Cloud Native Projects: Azure Serverless", type: "pdf" as const, file: "/c7.pdf" },
    { title: "Udemy University of Alberta Object-Oriented Design", type: "pdf" as const, file: "/c8.pdf" },
    { title: "Coursera Building Modern Python Applications on AWS", type: "pdf" as const, file: "/c9.pdf" },
    { title: "Building Modern Python Applications on AWS", type: "pdf" as const, file: "/c10.pdf" },
  ];

  const tataAMCCertificates: Certificate[] = [];

  const professionalTraining = [
    {
      name: "2+2 Coaching in Action",
      completedDate: "2/27/2025",
      details: "A coaching program focused on enhancing leadership and mentoring skills.",
    },
    {
      name: "Full Stack Upskilling ‐ React Programming Bootcamp ‐ Project Advanced ‐ React Form…",
      completedDate: "2/26/2025",
      details: "Advanced React programming bootcamp covering forms, state management, and project architecture.",
    },
    {
      name: "AC ISMS Annual Refresher Training Program FY 25",
      completedDate: "2/26/2025",
      details: "Annual refresher training on Information Security Management Systems (ISMS).",
    },
    {
      name: "FY25 One Firm Ethics, Independence, Risk and Compliance Training ‐ Administrative, …",
      completedDate: "2/4/2025",
      details: "Comprehensive training on ethics, independence, and compliance for administrative staff.",
    },
    {
      name: "Final assessment ‐ FY25 One Firm Ethics, Independence, Risk and Compliance Training…",
      completedDate: "2/4/2025",
      details: "Final assessment for the FY25 ethics and compliance training program.",
    },
    {
      name: "Personal independence webcast: Avoiding the most common personal independence…",
      completedDate: "1/31/2025",
      details: "Webcast on maintaining personal independence and avoiding common pitfalls.",
    },
    {
      name: "Art of 'I' ‐ I am assertive",
      completedDate: "1/28/2025",
      details: "Session on assertiveness and effective communication in professional settings.",
    },
    {
      name: "Cultural Agility Virtual Workshop",
      completedDate: "11/11/2024",
      details: "Workshop on developing cultural agility and working effectively in diverse teams.",
    },
    {
      name: "Prevention of Sexual Harassment Training ﴾ACs in India﴿",
      completedDate: "11/4/2024",
      details: "Mandatory training on the prevention of sexual harassment in the workplace.",
    },
    {
      name: "Career Development Immersion Experience Playback ‐ English",
      completedDate: "10/15/2024",
      details: "Playback session on career development strategies and experiences.",
    },
    {
      name: "Effective Communication Skills",
      completedDate: "9/20/2024",
      details: "Training focused on improving verbal and written communication skills in professional settings.",
    },
    {
      name: "Advanced Leadership Program",
      completedDate: "8/15/2024",
      details: "Comprehensive program on leadership strategies and team management.",
    },
    {
      name: "Data Privacy and Security Essentials",
      completedDate: "7/10/2024",
      details: "Training on best practices for ensuring data privacy and security in organizational workflows.",
    },
    {
      name: "Time Management Mastery",
      completedDate: "6/5/2024",
      details: "Workshop on effective time management techniques to enhance productivity.",
    },
    {
      name: "Conflict Resolution in Teams",
      completedDate: "5/25/2024",
      details: "Training on resolving conflicts within teams to foster collaboration and harmony.",
    },
    {
      name: "Agile Methodologies and Scrum Practices",
      completedDate: "4/15/2024",
      details: "In-depth training on Agile methodologies and Scrum practices for project management.",
    },
  ];

  const filteredTraining = professionalTraining.filter((training) =>
    training.name.toLowerCase().includes(searchQuery)
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: '16px' }}>
      <Box sx={{ maxWidth: '100%' }}>
        <Typography variant="h5" gutterBottom sx={{ marginBottom: '16px' }}>
          Work Experience
        </Typography>
        {experiences.map((exp, index) => (
          <Card 
            key={index}
            sx={{
              marginBottom: '8px',
              backgroundColor: '#f5f5f5',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start', paddingRight: '16px' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                    {exp.title}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                    {exp.subtitle}
                  </Typography>
                  {exp.description && (
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', marginTop: '4px' }}>
                      {exp.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {exp.title === "Associate Consultant" && (
                    <Tooltip title="Certificates">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EmojiEventsIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenITCCertificatesDialog();
                        }}
                      >
                        Certificates
                      </Button>
                    </Tooltip>
                  )}
                  {exp.title === "CTA" && (
                    <Tooltip title="Certificates">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EmojiEventsIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenPwCCertificatesDialog();
                        }}
                      >
                        Certificates
                      </Button>
                    </Tooltip>
                  )}
                  {exp.title === "Software Engineer Generative AI Lead" && (
                    <Tooltip title="Certificates">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EmojiEventsIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenTATAAMCCertificatesDialog();
                        }}
                      >
                        Certificates
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="More Info">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClickOpen(exp.dialogContent);
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {exp.title === "Project Intern" && (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ marginTop: '8px' }}
                  onClick={handleOpenTataSteelDialog}
                >
                  View Report
                </Button>
              )}
              {exp.title === "Research Project Member" && exp.subtitle.includes("Institute of Technical Education and Research") && (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ marginTop: '8px' }}
                  onClick={handleOpenResearchDialog}
                >
                  View Research
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
      {/* Tata Steel Report Dialog */}
      <ResumeDialog
        open={openTataSteelDialog}
        onClose={handleCloseTataSteelDialog}
        title="Tata Steel Internship Final Report"
        pdfUrl="/Tata_Steel_Internship_Final_Report.pdf"
      />
      {/* Research Paper Dialog */}
      <ResumeDialog
        open={openResearchDialog}
        onClose={handleCloseResearchDialog}
        title="Research Paper - Breast Cancer Classification"
        pdfUrl="/research.pdf"
      />
      <Box sx={{ maxWidth: '100%', padding: '16px' }}>
        <Typography variant="h5" gutterBottom>
          Professional Training
        </Typography>
        <TextField
          placeholder="Search Training"
          label="Search Training"
          variant="outlined"
          fullWidth
          margin="dense"
          onChange={handleSearchChange}
        />
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(100vh - 400px)',
            overflowY: 'auto',
            width: '100%',
            '@media (max-width: 600px)': {
              maxHeight: 'calc(100vh - 300px)',
              width: '100%',
            },
            '@media (max-width: 1024px)': {
              maxHeight: 'calc(100vh - 350px)',
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.875rem' }}>
                  <TableSortLabel>Training Name</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>
                  <TableSortLabel>Completed Date</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>
                  <TableSortLabel>Details</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTraining
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((training, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{training.name}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{training.completedDate}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{training.details}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleTrainingDialogOpen(training)}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredTraining.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[rowsPerPage]}
          />
        </TableContainer>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            position: 'absolute',
            width: '80%',
            height: '80%',
            top: '50%',
            left: '40%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '16px',
            boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.3)',
            backgroundColor: '#ffffff',
            padding: '16px',
            '@media (max-width: 500px)': {
              transform: 'translate(-50%, -50%)',
            },
            '@media (min-width: 501px) and (max-width: 1024px)': {
              maxWidth: '600px',
            },
            '@media (min-width: 1025px)': {
              maxWidth: '800px',
            },
          },
        }}
      >
        <AppBar sx={{ position: 'relative', backgroundColor: '#1976d2' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {dialogContent?.title}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ padding: '16px' }}>
          {dialogContent?.details && (
            <Typography variant="body1" gutterBottom>
              {dialogContent.details}
            </Typography>
          )}
          {dialogContent?.impacts &&
            dialogContent.impacts.map((impact, idx) => (
              <Accordion
                key={idx}
                expanded={expandedDialog === `panel${idx}`}
                onChange={handleDialogAccordionChange(`panel${idx}`)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`panel${idx}-content`} id={`panel${idx}-header`}>
                  <Typography variant="h6">{impact.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{impact.description}</Typography>
                  {impact.technologies && (
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      {impact.technologies.map((tech, index) => (
                        <Link key={index} href={tech.href} target="_blank" rel="noopener noreferrer">
                          <img src={tech.src} alt={tech.alt} width="24" height="24" />
                        </Link>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
        </Box>
      </Dialog>
      <Dialog
        open={trainingDialogOpen}
        onClose={handleTrainingDialogClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            width: '100vw',
            height: '80vh',
            position: 'absolute',
            top: '10%',
            left: 0,
            margin: 0,
            borderRadius: 0,
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            overflowY: 'auto',
            '@media (min-width: 515px)': {
              width: '80%',
              left: '10%',
            },
            '@media (min-width: 1024px)': {
              width: '60%',
              left: '20%',
            },
          },
        }}
      >
        <AppBar sx={{ position: 'relative', backgroundColor: '#1976d2' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleTrainingDialogClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Training Details
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ padding: '16px' }}>
          <Typography variant="h6">{selectedTraining?.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            Completed Date: {selectedTraining?.completedDate}
          </Typography>
          <Typography variant="body2" sx={{ marginTop: '8px' }}>
            {selectedTraining?.details}
          </Typography>
        </Box>
      </Dialog>
      
      {/* ITC Infotech Certificates Dialog */}
      <CertificateDialog
        open={openITCCertificatesDialog}
        onClose={handleCloseITCCertificatesDialog}
        title="ITC Infotech Certificates"
        certificates={itcCertificates}
      />
      
      {/* PwC Certificates Dialog */}
      <CertificateDialog
        open={openPwCCertificatesDialog}
        onClose={handleClosePwCCertificatesDialog}
        title="PwC Certificates"
        certificates={pwcCertificates}
      />
      
      {/* TATA AMC Certificates Dialog */}
      <CertificateDialog
        open={openTATAAMCCertificatesDialog}
        onClose={handleCloseTATAAMCCertificatesDialog}
        title="TATA AMC Certificates"
        certificates={tataAMCCertificates}
      />
    </Box>
  );
}

export default Experience;
